<template>
  <ts-modal
    ref="modal"
    v-if="confirmation"
    :modal-title="confirmation.title"
    modal-class="action-confirmation-modal"
  >
    <template #modal-content>
      <loading-indicator
        v-if="isSubmitting"
        :vertical="true"
        additional-css-class="mb-3"
        message="Performing action ..."
      />
      <alert
        :message="confirmation.message"
        :dismissable="false"
        additional-css-class="text-center"
        :type="confirmation.type"
      />
      <div
        v-if="requireConfirmationCode"
        class="confirmation"
      >
        <div class="code">
          Enter confirmation code <span class="required-code">{{ confirmation.confirmationCode }}</span> in the box below
        </div>
        <div class="mb-3">
          <input
            v-model="confirmationCode"
            type="text"
            :class="['form-control form-control-sm', {'is-invalid': confirmationCodeInvalid}]"
          >
          <div
            v-if="confirmationCodeInvalid"
            class="invalid-feedback"
          >
            Invalid confirmation code
          </div>
        </div>
      </div>
      <form
        :id="formId"
        :action="url"
        method="POST"
        style="display: none;"
        @submit="onConfirmAction"
      >
        <input
          type="hidden"
          :name="csrfParam"
          :value="csrfToken"
        >
        <input
          type="hidden"
          name="_method"
          :value="confirmation.method"
        >
        <input
          v-if="requireConfirmationCode"
          type="hidden"
          name="confirmation_code"
          :value="confirmationCode"
        >
      </form>
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
        :disabled="isSubmitting"
      >
        {{ confirmation.cancelActionTitle }}
      </button>
      <button
        :class="`btn btn-sm btn-${confirmation.type}`"
        type="submit"
        :form="formId"
        :disabled="isSubmitting"
      >
        {{ confirmation.confirmActionTitle }}
      </button>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import {ref, computed, useTemplateRef} from 'vue'
import TsModal from './ts-modal.vue'
import Alert from './alert.vue'
import LoadingIndicator from './loading-indicator.vue'
import Rails from '@rails/ujs'
import { isNonEmptyString } from '@/js/js-utils'
import type { ConfirmationConfiguration } from './types'

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')

const confirmationCode = ref('')
const confirmationCodeInvalid = ref(false)
const formId = ref('')
const url = ref('')
const isSubmitting = ref(false)
const confirmation = ref<ConfirmationConfiguration | null>(null)

const csrfParam = computed((): string => {
  return Rails.csrfParam() || ''
})

const csrfToken = computed((): string => {
  return Rails.csrfToken() || ''
})

const requireConfirmationCode = computed((): boolean => {
  return isNonEmptyString(confirmation.value?.confirmationCode)
})

const show = (confirmationConfig: ConfirmationConfiguration, urlValue: string): void => {
  formId.value = `action-confirmation-${new Date().getTime()}`
  isSubmitting.value = false
  confirmationCodeInvalid.value = false
  confirmation.value = confirmationConfig
  url.value = urlValue
  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const onConfirmAction = (e: Event): void => {
  confirmationCodeInvalid.value = false
  if (requireConfirmationCode.value && confirmationCode.value != confirmation.value?.confirmationCode) {
    e.preventDefault()
    confirmationCodeInvalid.value = true
    return
  }
  isSubmitting.value = true
}

defineExpose({
  show,
  hideModal
})
</script>
